import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import { CssHelper } from "~/helpers/css_helper"
import { cookieStore } from "cookie-store"

Chart.register(...registerables)

export default class extends Controller {
  static get targets() {
    return ["chart", "config", "values", "header"]
  }

  static get values() {
    return {
      index: Number,
      filters: Object,
      data: Array,
      attributeName: String,
      filterField: String,
      configKey: String,
    }
  }

  connect() {
    this._renderChart()
  }

  _getColors() {
    // Default colors
    const defaultColors = [
      CssHelper.getVar("--colors-success-600"),
      CssHelper.getVar("--colors-warning-600"),
      CssHelper.getVar("--colors-danger-600"),
      CssHelper.getVar("--colors-violette-500"),
      CssHelper.getVar("--colors-secondary-600"),
      CssHelper.getVar("--colors-grey-300"),
    ]

    // If this is verification_status, use contextual colors
    if (this.filterFieldValue === "verification_status") {
      return this.dataValue.map((bucket) => {
        const key = bucket.key

        // Map verification status keys to appropriate colors
        switch (key) {
          case "valid":
            return CssHelper.getVar("--colors-success-600")
          case "accept_all":
            return CssHelper.getVar("--colors-warning-600")
          case "invalid":
          case "error":
            return CssHelper.getVar("--colors-danger-600")
          case "disposable":
            return CssHelper.getVar("--colors-secondary-600")
          case "webmail":
            return CssHelper.getVar("--colors-magenta-500")
          case "unknown":
          case "pending":
            return CssHelper.getVar("--colors-grey-400")
          default:
            return CssHelper.getVar("--colors-grey-300")
        }
      })
    }

    // For other attributes, use default colors
    return defaultColors
  }

  _renderChart() {
    const ctx = this.chartTarget

    let dataNumbers = this.dataValue.map((x) => parseFloat(((x.bucket_count / x.total_count) * 100).toFixed(2)))
    let labels = this.dataValue.map((x) => x.name)
    const colors = this._getColors()

    const data = {
      labels,
      datasets: [
        {
          cutout: "70%",
          data: dataNumbers,
          borderJoinStyle: "round",
          backgroundColor: colors, // Use adjusted colors instead of original colors
        },
      ],
    }

    // Custom Tooltip
    const getOrCreateTooltip = (chart) => {
      let tooltipEl = chart.canvas.parentNode.querySelector("div")

      if (!tooltipEl) {
        tooltipEl = document.createElement("div")
        tooltipEl.classList.add("tooltip", "bs-tooltip-auto", "chart-tooltip")

        chart.canvas.parentNode.appendChild(tooltipEl)
      }

      return tooltipEl
    }

    const externalTooltipHandler = (context) => {
      // Tooltip Element
      const { chart, tooltip } = context
      const tooltipEl = getOrCreateTooltip(chart)

      // Hide if no tooltip
      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0
        return
      }

      // Set Text
      if (tooltip.body) {
        const titleLines = tooltip.title || []
        const bodyLines = tooltip.body.map((b) => b.lines)
        const tooltipRoot = tooltipEl

        const tooltipInner = document.createElement("div")
        tooltipInner.classList.add("tooltip-inner")

        titleLines.forEach((title, i) => {
          const titleWrapper = document.createElement("div")
          titleWrapper.classList.add("d-flex", "align-items-center", "gap-1")

          const colors = tooltip.labelColors[i]
          const titleMarker = document.createElement("div")
          titleMarker.style.background = colors.backgroundColor
          titleMarker.classList.add("adv-card-legend__marker")

          const tooltipTitle = document.createElement("strong")
          tooltipTitle.classList.add("fw-medium")
          tooltipTitle.innerText = title

          titleWrapper.appendChild(titleMarker)
          titleWrapper.appendChild(tooltipTitle)
          tooltipInner.appendChild(titleWrapper)
        })

        bodyLines.forEach((body, bodyIndex) => {
          const bodyWrapper = document.createElement("div")

          const dataIndex = tooltip.dataPoints[bodyIndex].dataIndex
          const percentage = dataNumbers[dataIndex]
          const bucketCount = this.dataValue[dataIndex].bucket_count

          const bodyText = document.createTextNode(`${percentage.toFixed(1)}% - ${bucketCount.toLocaleString()} leads`)
          bodyWrapper.appendChild(bodyText)

          tooltipInner.appendChild(bodyWrapper)
        })

        // Remove old children
        while (tooltipRoot.firstChild) {
          tooltipRoot.firstChild.remove()
        }

        // Add new children
        tooltipRoot.appendChild(tooltipInner)
      }

      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

      // Display, position, and set styles for font
      tooltipEl.style.opacity = 1
      tooltipEl.style.left = positionX + tooltip.caretX + "px"
      tooltipEl.style.top = positionY + tooltip.caretY + "px"
      tooltipEl.style.font = tooltip.options.bodyFont.string
      tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px"
    }

    const borderPlugin = {
      id: "arcBorder",
      beforeDraw(chart) {
        const { ctx } = chart
        const meta = chart.getDatasetMeta(0)
        const currentChartFilters = this.filtersValue[this.filterFieldValue] || []
        const anyFilters = currentChartFilters.length > 0
        const anyHovered = meta.data.some((arc) => arc.active)
        const filterSelected = (chartFilters, filterValue) => {
          if (chartFilters.length === 0) {
            return false
          }

          if (Array.isArray(filterValue) && Array.isArray(chartFilters)) {
            return filterValue.every((value) => chartFilters.includes(String(value)))
          } else if (Array.isArray(chartFilters)) {
            return chartFilters.includes(filterValue)
          } else {
            return chartFilters === filterValue
          }
        }

        // First pass: handle opacity
        meta.data.forEach((arc, index) => {
          const isSelected = filterSelected(currentChartFilters, this.dataValue[index].filter_value)
          const isHovered = arc.active

          // Update arc opacity based on selection and hover state
          if (anyFilters) {
            // Existing filter behavior
            if (!isSelected && !isHovered) {
              arc.options.backgroundColor = this._getTransparentColor(colors[index], 0.5)
            } else {
              arc.options.backgroundColor = colors[index]
            }
          } else if (anyHovered) {
            // No filters, but something is hovered
            if (!isHovered) {
              arc.options.backgroundColor = this._getTransparentColor(colors[index], 0.8)
            } else {
              arc.options.backgroundColor = colors[index]
            }
          } else {
            // No filters, no hover
            arc.options.backgroundColor = colors[index]
          }
        })

        // Second pass: draw borders
        meta.data.forEach((arc, index) => {
          const isSelected = filterSelected(currentChartFilters, this.dataValue[index].filter_value)
          if (!isSelected) return

          // Check if previous and next segments are selected
          const prevIndex = (index - 1 + meta.data.length) % meta.data.length
          const nextIndex = (index + 1) % meta.data.length
          const isPrevSelected = filterSelected(currentChartFilters, this.dataValue[prevIndex].filter_value)
          const isNextSelected = filterSelected(currentChartFilters, this.dataValue[nextIndex].filter_value)

          ctx.save()
          ctx.beginPath()

          // Calculate arc length and adjust border width accordingly
          const arcLength = Math.abs(arc.endAngle - arc.startAngle)
          const defaultBorderWidth = 5
          const minBorderWidth = 1
          const borderWidth = Math.max(minBorderWidth, Math.min(defaultBorderWidth, (arcLength * arc.outerRadius) / 2))

          // Adjust start and end angles based on adjacent selections
          let startAngle = arc.startAngle
          let endAngle = arc.endAngle

          if (!isPrevSelected) {
            startAngle += Math.asin(borderWidth / (2 * arc.outerRadius))
          }

          if (!isNextSelected) {
            endAngle -= Math.asin(borderWidth / (2 * arc.outerRadius))
          }

          ctx.arc(arc.x, arc.y, arc.outerRadius, startAngle, endAngle)

          ctx.strokeStyle = colors[index]
          ctx.lineWidth = borderWidth
          ctx.stroke()
          ctx.restore()
        })
      },
      filtersValue: this.filtersValue,
      dataValue: this.dataValue,
      filterFieldValue: this.filterFieldValue,
      _getTransparentColor(hexColor, opacity) {
        const r = parseInt(hexColor.slice(1, 3), 16)
        const g = parseInt(hexColor.slice(3, 5), 16)
        const b = parseInt(hexColor.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${opacity})`
      },
    }

    const config = {
      type: "doughnut",
      data: data,
      options: {
        responsive: true,
        layout: {
          padding: {
            top: 4,
            right: 4,
            bottom: 4,
            left: 4,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            position: "nearest",
            external: externalTooltipHandler.bind(this),
          },
        },
        animation: {
          duration: 0,
        },
        hover: {
          mode: "index",
        },
        onClick: async (_, elements) => {
          const url = this.dataValue[elements[0].index].url
          this._changeAttribute(url)
        },
      },
      plugins: [borderPlugin],
    }

    this.chart = new Chart(ctx, config)
  }

  labelClick(event) {
    const url = event.params.url
    this._changeAttribute(url)
  }

  async _changeAttribute(url) {
    await this._lockCard()
    this._grayOutCard()

    Turbo.visit(url, { action: "advance", frame: "_top" })
  }

  showConfig() {
    this.configTarget.classList.remove("is-hidden")
    this.configTarget.classList.add("is-visible")
  }

  hideConfig() {
    this.configTarget.classList.remove("is-visible")

    setTimeout(() => {
      this.configTarget.classList.add("is-hidden")
    }, 250)
  }

  _grayOutCard() {
    this.element.classList.add("adv-card--loading")
    this.chart.destroy()
    this.chartTarget.getContext("2d").clearRect(0, 0, this.chartTarget.width, this.chartTarget.height)
    this.headerTarget.innerHTML = ""
    this.valuesTarget.innerHTML = ""
  }

  async _lockCard() {
    const store = window.cookieStore || cookieStore
    const cookie = await store.get(this.configKeyValue)
    let cookieValue = JSON.parse(decodeURIComponent(cookie.value))
    if (cookieValue[this.indexValue].filters) {
      // If filters are already set then we don't want to override it since card is locked already
      return
    }

    cookieValue[this.indexValue].filters = this.filtersValue
    await store.set(this.configKeyValue, JSON.stringify(cookieValue))
  }
}
